import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { TESTIDS } from "@/constants/testIds";
import { ShieldCheck, FlaskConical, Home as HomeIcon, ArrowRight, ListChecks } from "lucide-react";
import { t } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1565870100382-f0a510db3cd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjb3VwbGUlMjBvbiUyMGNvdWNoJTIwdXNpbmclMjBwaG9uZXxlbnwwfHx8fDE3ODExNzk2MTV8MA&ixlib=rb-4.1.0&q=85";

const Landing = () => {
  const { user } = useAuth();
  const authed = user && typeof user === "object";

  const modules = [
    {
      icon: HomeIcon,
      title: t("Know"),
      body: t("Learn about radon, how it enters homes, and why testing matters."),
      to: "/learn",
    },
    {
      icon: FlaskConical,
      title: t("Test"),
      body: t("Log every reading in one place — kits, monitors, professional tests."),
      to: "/signup",
    },
    {
      icon: ListChecks,
      title: t("Act"),
      body: t("Get a personalized checklist based on your latest pCi/L reading."),
      to: "/signup",
    },
    {
      icon: ShieldCheck,
      title: t("Fix & Track"),
      body: t("Record mitigation, verify with a post-test, and monitor over time."),
    },
  ];

  return (
    <div>
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 fade-up">
            <p className="eyebrow mb-6">{t("Radon Action & Tracking · Since 2026")}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1C201F]">
              {t("Know Your Radon Level.")}
              <span className="block italic text-[#2F524A]">{t("Know What to Do Next.")}</span>
            </h1>
            <p className="mt-7 text-lg text-[#5C6661] leading-relaxed max-w-xl">
              {t("Enter your radon reading or complete a one-minute assessment to understand your home’s radon risk and receive a personalized action plan.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={authed ? "/dashboard?add=1" : "/signup?next=add"}
                data-testid={TESTIDS.heroCtaReading}
                className="btn-primary inline-flex items-center gap-2"
              >
                {t("Enter a Radon Reading")}
                <ArrowRight size={18} />
              </Link>
              <Link
                to={authed ? "/dashboard" : "/signup"}
                data-testid={TESTIDS.heroCtaNotTested}
                className="btn-secondary"
              >
                {t("I haven’t tested yet")}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-[#5C6661]">
              <span className="eyebrow">{t("Free to use")}</span>
              <span className="eyebrow">{t("Private & secure")}</span>
              <span className="eyebrow">{t("Track your Radon Record over time")}</span>
            </div>
          </div>

          <div className="lg:col-span-6 fade-up delay-2">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#F4F7F6]" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-[#E8ECEA] grain-overlay">
                <img
                  src={HERO_IMG}
                  alt={t("Homeowners reviewing their radon record on a couch")}
                  className="w-full h-[460px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 card-flat shadow-sm hidden sm:block w-72">
                <p className="eyebrow">{t("Latest reading")}</p>
                <p className="font-serif text-4xl text-[#1C201F] mt-1">
                  5.8 <span className="text-base text-[#5C6661] font-sans">{t("pCi/L")}</span>
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#E07A5F" }} />
                  <span className="text-sm text-[#1C201F]">{t("Above EPA Action Level")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <p className="eyebrow mb-4">{t("You can’t see, smell, or taste radon")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl max-w-3xl leading-tight text-[#1C201F]">
          {t("But you can test for it. Radon is the second-leading cause of lung-cancer deaths in the United States.")}
          <span className="text-[#5C6661]">
            {" "}{t("Testing is the only way to know your home’s radon level.")}
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
          {modules.map((m, i) => {
            const Icon = m.icon;
            const Wrap = m.to ? Link : "div";
            const wrapProps = m.to
              ? { to: m.to, "data-testid": `module-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}` }
              : {};
            return (
              <Wrap
                key={m.title}
                className={`card-flat fade-up delay-${i + 1} ${
                  m.to ? "cursor-pointer hover:-translate-y-0.5 transition-transform block" : ""
                }`}
                {...wrapProps}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center mb-5"
                  style={{ background: "#F4F7F6", color: "#2F524A" }}
                >
                  <Icon size={20} />
                </div>
                <h3 className="font-serif text-xl text-[#1C201F]">{m.title}</h3>
                <p className="text-[#5C6661] mt-2 text-sm leading-relaxed">{m.body}</p>
                {m.to && (
                  <p className="mt-3 text-xs text-[#2F524A] font-semibold">
                    {t("Learn more →")}
                  </p>
                )}
              </Wrap>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Landing;
